import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Shield, User, LogOut, Menu, X, Crown, Zap } from 'lucide-react';
import AuthModal from './AuthModal';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isPro, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTab, setAuthTab] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const openAuth = (tab) => {
    setAuthTab(tab);
    setShowAuthModal(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <nav className="border-b border-gray-800 bg-black/50 backdrop-blur-sm fixed w-full z-50 top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-8">
              <div 
                className="flex items-center space-x-2 cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <Shield className="w-8 h-8 text-[#00FF00]" />
                <span className="text-xl font-bold text-white">CyberInsti</span>
              </div>
              
              <div className="hidden md:flex items-center space-x-1">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/dashboard')}
                  className={`${
                    isActive('/dashboard') 
                      ? 'text-[#00FF00] bg-[#00FF00]/10' 
                      : 'text-gray-300 hover:text-white'
                  } hover:bg-gray-800`}
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/paths')}
                  className={`${
                    isActive('/paths') 
                      ? 'text-[#00FF00] bg-[#00FF00]/10' 
                      : 'text-gray-300 hover:text-white'
                  } hover:bg-gray-800`}
                >
                  Learning Paths
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/rooms')}
                  className={`${
                    isActive('/rooms') 
                      ? 'text-[#00FF00] bg-[#00FF00]/10' 
                      : 'text-gray-300 hover:text-white'
                  } hover:bg-gray-800`}
                >
                  Rooms
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/leaderboard')}
                  className={`${
                    isActive('/leaderboard') 
                      ? 'text-[#00FF00] bg-[#00FF00]/10' 
                      : 'text-gray-300 hover:text-white'
                  } hover:bg-gray-800`}
                >
                  Leaderboard
                </Button>
                <Button
                  data-testid="nav-pricing-btn"
                  variant="ghost"
                  onClick={() => navigate('/pricing')}
                  className={`${
                    isActive('/pricing')
                      ? 'text-[#00FF00] bg-[#00FF00]/10'
                      : 'text-gray-300 hover:text-white'
                  } hover:bg-gray-800`}
                >
                  Pricing
                </Button>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              {isAuthenticated ? (
                <>
                  {isPro ? (
                    <Badge
                      data-testid="nav-pro-badge"
                      className="hidden sm:inline-flex bg-[#00FF00]/15 text-[#00FF00] border border-[#00FF00]/40"
                    >
                      <Crown className="w-3 h-3 mr-1" />
                      PRO
                    </Badge>
                  ) : (
                    <Button
                      data-testid="nav-upgrade-btn"
                      size="sm"
                      onClick={() => navigate('/pricing')}
                      className="hidden sm:inline-flex bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Upgrade
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/profile')}
                    className="text-gray-300 hover:text-white hover:bg-gray-800 flex items-center"
                  >
                    <User className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">{user?.username || 'Profile'}</span>
                  </Button>
                  <Button
                    data-testid="nav-logout-btn"
                    variant="outline"
                    onClick={handleLogout}
                    className="border-red-500/50 text-red-400 hover:text-red-300 hover:bg-red-500/10 hover:border-red-500"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5 sm:mr-2" />
                    <span className="hidden sm:inline">Logout</span>
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    data-testid="nav-signin-btn"
                    variant="ghost"
                    onClick={() => openAuth('login')}
                    className="text-gray-300 hover:text-white hover:bg-gray-800"
                  >
                    Sign In
                  </Button>
                  <Button
                    data-testid="nav-signup-btn"
                    onClick={() => openAuth('register')}
                    className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                  >
                    Get Started
                  </Button>
                </>
              )}
              
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                className="md:hidden text-gray-300 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-800 py-4">
              <div className="flex flex-col space-y-2">
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className={`justify-start ${isActive('/dashboard') ? 'text-[#00FF00]' : 'text-gray-300'}`}
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/paths'); setMobileMenuOpen(false); }}
                  className={`justify-start ${isActive('/paths') ? 'text-[#00FF00]' : 'text-gray-300'}`}
                >
                  Learning Paths
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/rooms'); setMobileMenuOpen(false); }}
                  className={`justify-start ${isActive('/rooms') ? 'text-[#00FF00]' : 'text-gray-300'}`}
                >
                  Rooms
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }}
                  className={`justify-start ${isActive('/leaderboard') ? 'text-[#00FF00]' : 'text-gray-300'}`}
                >
                  Leaderboard
                </Button>
                <Button
                  data-testid="nav-pricing-mobile-btn"
                  variant="ghost"
                  onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                  className={`justify-start ${isActive('/pricing') ? 'text-[#00FF00]' : 'text-gray-300'}`}
                >
                  Pricing
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          initialTab={authTab}
        />
      )}
    </>
  );
};

export default Navbar;
